import { __ } from "@wordpress/i18n";
import React, { useEffect, useRef, useState } from "react";
import "./MultiSelect.scss";
import ArrowDown from "../../assets/images/ArrowDown.svg";
const MultiSelect = ({ 
    name,
    options = [], 
    selectedValues = [], 
    placeholder = "Select options...",
    onChange, 
    disabled = false 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef(null);
    
    // Filter options based on search input
    const filteredOptions = options.filter(option => {
        const label = option.label || option.value || "";
        return label.toLowerCase().includes(search.toLowerCase());
    });
    
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    
    // Handle toggling of a specific value
    const toggleValue = (value) => {
        if (disabled) return;
        
        const isSelected = selectedValues.includes(value);
        let newSelectedValues;
        
        if (isSelected) {
            // Remove the value
            newSelectedValues = selectedValues.filter(v => v !== value);
        } else {
            // Add the value
            newSelectedValues = [...selectedValues, value];
        }
        
        if (onChange) {
            onChange(name, newSelectedValues);
        }
    };
    
    // Handle select all
    const handleSelectAll = () => {
        if (disabled) return;
        
        const allValues = options.map(option => option.value);
        
        if (onChange) {
            onChange(name, allValues);
        }
    };
    
    // Handle remove all
    const handleRemoveAll = () => {
        if (disabled) return;
        
        if (onChange) {
            onChange(name, []);
        }
    };
    
    // Get label for a value
    const getLabelForValue = (value) => {
        const option = options.find(option => option.value === value);
        return option ? (option.label || option.value) : value;
    };
    
    return (
        <div className={`multi-select-wrapper ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
            {/* Selected items display */}
            <div 
                className="multi-select-header"
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {selectedValues.length === 0 ? (
                    <div className="placeholder">{placeholder}</div>
                ) : (
                    <div className="selected-items">
                        {selectedValues.map(value => (
                            <div key={value} className="selected-item">
                                <span className="item-label">{getLabelForValue(value)}</span>
                                <span 
                                    className="remove-item"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleValue(value);
                                    }}
                                >
                                    ×
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                <img src={ArrowDown} className="dropdown-icon" alt="icon" />
                {/* <div className="dropdown-icon">▼</div> */}
            </div>
            
            {/* Dropdown */}
            {isOpen && (
                <div className="multi-select-dropdown">
                    {/* Search box */}
                    <div className="search-box">
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search..."
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    
                    {/* Options list */}
                    <div className="options-list">
                        {filteredOptions.length === 0 ? (
                            <div className="no-options">No options found</div>
                        ) : (
                            filteredOptions.map(option => (
                                <div 
                                    key={option.value} 
                                    className={`option ${selectedValues.includes(option.value) ? 'selected' : ''}`}
                                    onClick={() => toggleValue(option.value)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedValues.includes(option.value)}
                                        onChange={() => {}} // Handled by div click
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span className="option-label">{option.label || option.value}</span>
                                </div>
                            ))
                        )}
                    </div>
                    
                    {/* Action buttons */}
                    {options.length > 0 && (
                        <div className="actions">
                            <button 
                                type="button"
                                className="select-all"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectAll();
                                }}
                            >
                                {__("Select All", "ultimate-security")}
                            </button>
                            <button 
                                type="button"
                                className="remove-all"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveAll();
                                }}
                            >
                                {__("Remove All", "ultimate-security")}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


export default MultiSelect;